import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
} from '@mui/material'
import { X } from 'lucide-react'
import { useMobile } from '../../../hooks/useMobile'
import { MobileModalProps } from './types'

/**
 * MobileModal - Responsive modal optimized for mobile
 *
 * Features:
 * - Full screen on mobile (<768px)
 * - Standard dialog on desktop
 * - Close button in header (mobile)
 * - Fixed header and footer on mobile
 * - Scrollable content area
 * - iOS safe area support
 *
 * @example
 * ```tsx
 * <MobileModal
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Edit Product"
 *   actions={
 *     <>
 *       <Button onClick={onClose}>Cancel</Button>
 *       <Button variant="contained">Save</Button>
 *     </>
 *   }
 * >
 *   <ProductForm />
 * </MobileModal>
 * ```
 */
export const MobileModal: React.FC<MobileModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
}) => {
  const { isMobile } = useMobile()

  if (isMobile) {
    // Mobile: Full-screen modal
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            maxHeight: '100vh',
          },
        }}
      >
        {/* Header with close button */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
            }}
          >
            {title}
            <IconButton
              edge="end"
              onClick={onClose}
              aria-label="Fermer"
              sx={{ marginLeft: 'auto' }}
            >
              <X size={24} />
            </IconButton>
          </DialogTitle>
        </Box>

        {/* Scrollable content */}
        <DialogContent sx={{ flex: 1, overflow: 'auto' }}>{children}</DialogContent>

        {/* Fixed footer */}
        {actions && (
          <DialogActions
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              paddingBottom: 'env(safe-area-inset-bottom)',
              padding: 2,
            }}
          >
            {actions}
          </DialogActions>
        )}
      </Dialog>
    )
  }

  // Desktop: Standard dialog
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  )
}

// Display name for debugging
MobileModal.displayName = 'MobileModal'
